import React, { useState, useEffect } from 'react';
import { View, Text } from '@adobe/react-spectrum';
import { QuillEditor } from './QuillEditor';
import { FormField as StoreFormField } from '../../store/slice';
import styles from '../FormField.module.scss';
import ToolTipWrapper from '../../../ToolTipWrapper/ToolTipWrapper';
import { getTimezoneAbbreviation } from '../../../../../utils/time';

interface RichTextEditorProps {
  field: StoreFormField;
  value: string;
  onChange: (name: string, value: string) => void;
  hideLabel?: boolean;
  footer?: React.ReactNode;
  isInvalid?: boolean;
  externalTimestamp?: string; // Optional external timestamp to use instead of lastSaved
  minHeight?: string; // Optional minimum height for the editor
  disabled?: boolean; // RBAC: Disable editing when user doesn't have permission
  readOnly?: boolean; // RBAC: Alternative to disabled for read-only mode
}

/**
 * Rich text editor component using Quill
 */
export const RichTextEditorComponent: React.FC<RichTextEditorProps> = ({
  field,
  value = '',
  onChange,
  hideLabel = false,
  footer,
  isInvalid,
  externalTimestamp,
  minHeight = '200px',
  disabled = false,
  readOnly = false
}) => {
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [, setCharCount] = useState(0);

  // Update charCount when content changes
  useEffect(() => {
    // Remove trailing newline if present
    // Ensure value is a string before checking length
    const stringValue = typeof value === 'string' ? value : '';
    const contentLength = stringValue
      ? stringValue.length - (stringValue.endsWith('\n') ? 1 : 0)
      : 0;
    setCharCount(contentLength);
  }, [value]);

  // Handle content change
  const handleEditorChange = (content: string) => {
    onChange(field.name, content);
  };

  // Ensure value is always a string for the editor
  const safeValue = typeof value === 'string' ? value : '';

  // Format the last saved timestamp
  const formatTimestamp = (timestamp?: string | Date | null) => {
    if (!externalTimestamp && !lastSaved) return '';
    try {
      let date: Date;

      // Use external timestamp if provided, otherwise use lastSaved
      if (externalTimestamp) {
        date = new Date(externalTimestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        date = lastSaved!;
      }

      // Manual formatting for consistent cross-browser display
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];

      const month = months[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12; // Convert to 12-hour format

      return `Last updated: ${month} ${day}, ${year}, ${hours}:${minutes} ${ampm}`;
    } catch (error) {
      return 'Last updated: N/A';
    }
  };

  if (hideLabel) {
    return (
      <View UNSAFE_className={styles.richTextEditorContainer}>
        <View
          UNSAFE_className={styles.quillWrapper}
          UNSAFE_style={{ display: 'flex' }}
        >
          <QuillEditor
            value={safeValue}
            onChange={handleEditorChange}
            placeholder="Enter detailed content here..."
            charLimit={
              typeof (field as any)?.charLimit === 'number' ||
              (field as any)?.charLimit === null
                ? (field as any).charLimit
                : null
            }
            showCharCount={true}
            className={styles.fullEditor}
            onCharCountChange={setCharCount}
            readOnly={disabled || readOnly}
            autoFocus={true}
            minHeight={minHeight}
            footerLeft="Characters remaining"
            footerCenter={null}
            footerRight={
              <>
                <span className={styles.savedTimestamp}>
                  {formatTimestamp(lastSaved)}
                </span>
                {React.isValidElement(footer) && typeof footer.type !== 'string'
                  ? React.cloneElement(
                      footer as React.ReactElement<{
                        onClick?: (e: React.MouseEvent) => void;
                      }>,
                      {
                        onClick: (e: React.MouseEvent) => {
                          setLastSaved(new Date());
                          if (
                            footer.props &&
                            typeof footer.props.onClick === 'function'
                          ) {
                            footer.props.onClick(e);
                          }
                        }
                      }
                    )
                  : footer || null}
              </>
            }
          />
        </View>
      </View>
    );
  }

  return (
    <View
      UNSAFE_className={styles.richTextEditorContainer}
      data-invalid={isInvalid ? 'true' : undefined}
    >
      {/* <ToolTipWrapper message={field.toolTipMessage}> */}
      <View UNSAFE_className={styles.labelContainer}>
        <Text UNSAFE_className={styles.fieldLabel}>
          {field.label}
          {field.required && <span className={styles.required}> *</span>}
        </Text>
      </View>
      {/* </ToolTipWrapper> */}

      <div className={styles.editorContainer}>
        <View
          UNSAFE_className={styles.quillWrapper}
          UNSAFE_style={{ display: 'flex' }}
        >
          <QuillEditor
            value={safeValue}
            onChange={handleEditorChange}
            placeholder={field.toolTipMessage}
            // placeholder="Enter detailed content here..."
            charLimit={
              typeof (field as any)?.charLimit === 'number' ||
              (field as any)?.charLimit === null
                ? (field as any).charLimit
                : null
            }
            showCharCount={true}
            className={styles.fullEditor}
            readOnly={disabled || readOnly}
            onCharCountChange={setCharCount}
            minHeight={minHeight}
            footerLeft="Characters remaining"
            footerRight={footer}
          />
        </View>
      </div>
    </View>
  );
};
